import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Loader, Sparkles, FileText } from 'lucide-react';
import securityApi from '../../../api/securityApi';
import toast from 'react-hot-toast';
import PolicyRow from './PolicyRow';

const PoliciesList = ({ projectId }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null);
  const pollingRef = useRef(null);

  const fetchPolicies = useCallback(async () => {
    try {
      const response = await securityApi.listProjectPolicies(projectId);
      setPolicies(response.policies || response || []);
    } catch (err) {
      console.error('Error fetching policies:', err);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const handleGenerateAll = async () => {
    setGenerating(true);
    setGenerationProgress(null);
    try {
      await securityApi.generateAllPolicies(projectId);

      // Start polling for generation status
      pollingRef.current = setInterval(async () => {
        try {
          const status = await securityApi.getPolicyGenerationStatus(projectId);
          setGenerationProgress(status);

          if (status.status === 'completed' || status.status === 'failed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setGenerating(false);
            setGenerationProgress(null);

            if (status.status === 'completed') {
              toast.success('All policies generated successfully');
            } else {
              toast.error('Policy generation failed');
            }
            fetchPolicies();
          }
        } catch (err) {
          // If polling fails, stop and refetch
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGenerating(false);
          setGenerationProgress(null);
          fetchPolicies();
        }
      }, 3000);
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || 'Failed to generate policies');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
        <span className="text-sm text-gray-600">
          <span className="font-medium">{policies.length}</span> {policies.length === 1 ? 'Policy' : 'Policies'}
        </span>
        <button
          onClick={handleGenerateAll}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
        >
          {generating ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate All Policies
            </>
          )}
        </button>
      </div>

      {/* Generation progress */}
      {generating && generationProgress && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-teal-700">
              Generating policies...
            </span>
            {generationProgress.progress != null && (
              <span className="text-sm text-teal-600">
                {Math.round(generationProgress.progress)}%
              </span>
            )}
          </div>
          {generationProgress.progress != null && (
            <div className="bg-teal-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${generationProgress.progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Policy list */}
      {policies.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Policies Yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Generate policies based on your gap analysis to get started.
          </p>
          <button
            onClick={handleGenerateAll}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            Generate All Policies
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {policies.map((policy) => (
            <PolicyRow
              key={policy.id}
              policy={policy}
              projectId={projectId}
              onPolicyUpdated={fetchPolicies}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PoliciesList;
